<template>
    <div class="">
        <p class="mb-0 text-muted text-uppercase font-weight-bold">
            Method: {{ goal.parameters.method.replace(/_/g, " ") }}
        </p>
        <div class="row px-3 pb-3">
            <div class="col-8 p-1">
                <prompt :iconset="6" :session="true" @select-prompt="addAttempt($event)"></prompt>
            </div>

            <div class="col-4 p-1">
                <div
                    class="position-relative d-flex flex-column justify-content-between align-items-center h-100 mt-2 mt-md-0"
                >
                    <div
                        :id="`timer-${goal ? goal.id : 0}`"
                        class="timer prog-circle d-flex flex-column justify-content-center align-center position-relative w-100 cursor-pointer"
                        @click="startTimer()"
                    >
                        <h3 class="position-absolute font-weight-bold mb-0 w-100 text-center">
                            {{ displayTime }}
                        </h3>
                    </div>
                </div>
                <audio id="ping" src="/sounds/341871__edsward__ping.wav"></audio>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <p class="mb-0 mt-2 text-primary text-uppercase font-weight-bold">
                    {{ currentInterval }}/{{ goal.parameters.intervals }} -
                    {{
                        moment()
                            .startOf("day")
                            .seconds(goal.parameters.interval_length)
                            .format("H:mm:ss")
                    }}
                    intervals
                </p>
            </div>
        </div>

        <div class="row p-3">
            <div class="col-9 pl-0" style="border-left:solid 1px #8f8f8f">
                <ul class="attempt-list pl-0">
                    <li v-for="(b, i) in results">
                        <span
                            class="attempt-icon ml-1 d-flex justify-content-start align-items-center"
                        >
                            <i
                                :class="assignToInterval === i + 1 ? 'fa-caret-right' : ''"
                                class="fa fa-fw text-primary mr-2"
                            ></i>
                            {{ i + 1 }}.&nbsp;
                            <prompt
                                v-if="b || b === 0"
                                :key="i"
                                :mini="true"
                                :prompt="[null, 6, b]"
                            ></prompt>
                        </span>
                    </li>
                </ul>
            </div>

            <div class="col-3">
                <h4 class="pl-1">{{ successRate }}%</h4>
            </div>
        </div>
    </div>
</template>

<script>
import Prompt from "../../ui/PromptIcon";

export default {
    name: "TimeSampling",
    props: ["goal", "date", "notesData"],
    components: { Prompt },
    data() {
        return {
            startTime: this.moment(),
            currentTime: this.moment(),
            pausedTime: 0,
            running: false,
            timerMax: this.goal.parameters.interval_length,
            progress: null,
            currentInterval: 1,
            assignToInterval: 1,
            results: [],
        };
    },

    watch: {
        elapsedTime() {
            if (this.elapsedTime >= this.timerMax) {
                this.startTime = this.moment();
                this.playSound();
                if (this.currentInterval < this.goal.parameters.intervals) {
                    this.prepareNextInterval();
                } else {
                    this.stopTimer();
                }
            }
        },
    },

    computed: {
        elapsedTime() {
            return Math.max(0, this.currentTime.diff(this.startTime, "seconds"));
        },
        displayTime() {
            const remaining = Math.min(this.timerMax - this.elapsedTime, this.timerMax);
            const hours = Math.max(Math.floor(remaining / 3600), 0);
            const minutes = Math.max(Math.floor((remaining % 3600) / 60), 0);
            const seconds = Math.max(remaining % 60, 0);
            return `${hours}:${("0" + minutes).slice(-2)}:${("0" + seconds).slice(-2)}`;
        },
        successRate() {
            if (this.results.length) {
                return parseInt((100 * this.results.filter(r => r).length) / this.results.length);
            }
        },
    },

    mounted() {
        this.$forceUpdate();
        this.progress = new this.ProgressBar.Circle(`#timer-${this.goal ? this.goal.id : 0}`, {
            strokeWidth: 7,
            easing: "easeInOut",
            duration: 1,
            color: "#b7dbf0",
            trailColor: "#50a3d7",
            trailWidth: 7,
            svgStyle: null,
        });
        this.currentInterval = 0;
        this.assignToInterval = 1;
    },

    methods: {
        prepareNextInterval() {
            this.running = false;
            const context = this;
            const pauseLength = 5000;
            setTimeout(() => {
                context.startTime.add(pauseLength, "ms");
                context.running = true;
                context.currentInterval++;
                context.assignToInterval++;
            }, pauseLength);
        },

        startTimer() {
            this.currentInterval = 1;
            this.running = true;
            this.startTime = this.moment();
            this.interval = setInterval(this.animate, 1000);
        },

        stopTimer() {
            this.running = false;
            clearInterval(this.interval);
        },

        animate() {
            if (this.running) {
                this.currentTime = this.moment();
                this.progress.animate(
                    Math.max(this.timerMax - this.elapsedTime, 0) / this.timerMax
                );
            }
        },

        addAttempt(event) {
            if (this.currentInterval) {
                this.$set(this.results, this.assignToInterval - 1, event[0]);
            }
            if (this.assignToInterval < this.currentInterval) {
                this.assignToInterval++;
            }
        },

        undoAttempt() {
            this.$set(this.results, this.assignToInterval - 1, null);
            if (this.assignToInterval > 1) {
                this.assignToInterval--;
            }
        },

        saveTrackerData() {
            let data = {
                goal_id: this.goal.id,
                data: {
                    type: "time_sampling",
                    success: this.results.filter(r => r).length,
                },
                is_flagged: this.flagData,
                notes: this.notesData,
                session_at: this.date
                    ? this.moment(this.date).format("YYYY-MM-DD HH:mm:ss")
                    : this.moment().format("YYYY-MM-DD HH:mm:ss"),
            };
            axios
                .post(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`, data)
                .then(r => {
                    this.reportResult(true);
                })
                .catch(error => {
                    this.$emit("save-draft", { error, data });
                    this.reportResult(false);
                    this.$swal({
                        title: "Error!",
                        text: "An error occurred while saving.",
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });
                    console.error(error);
                });
        },

        reportResult(success) {
            this.$emit("saved", success);
            if (success) {
                this.$emit(
                    "close-tracker",
                    this.goal.title +
                        `: ${this.results.filter(r => r).length} success in ${
                            this.goal.parameters.intervals
                        } intervals - ` +
                        this.notesData
                );
            }
        },

        playSound() {
            const audio = document.getElementById("ping");
            if (!audio) return;
            audio.currentTime = 0;
            audio.play();
        },
    },
};
</script>

<style scoped lang="scss">
.attempt-list {
    list-style: none;
}

h4 {
    border-left: solid 1px #8f8f8f;
}
</style>
